import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Gauge, TrendingDown, Leaf, Wallet, TrendingUp, ArrowRight, CheckCircle2,
  Lightbulb, AlertTriangle, FileBarChart, Zap, Fuel, BadgeCheck, Sparkles,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { ScoreGauge } from "@/components/ScoreGauge";
import { api, fmtEur, fmtNum } from "@/lib/api";
import { Button } from "@/components/ui/button";

const dotColor = (lvl) => lvl === "green" ? "text-gold-400" : lvl === "orange" ? "text-amber-400" : lvl === "red" ? "text-red-400" : "text-blue-400";
const dotBg = (lvl) => lvl === "green" ? "bg-gold-400" : lvl === "orange" ? "bg-amber-400" : lvl === "red" ? "bg-red-400" : "bg-blue-400";

const MetricCard = ({ icon: Icon, label, value, level, delay }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
    className="card-dark rounded-2xl p-5 relative overflow-hidden hover:border-white/15 transition-colors">
    <span className={`absolute top-5 right-5 h-2.5 w-2.5 rounded-full ${dotBg(level)}`} />
    <Icon className={`h-5 w-5 mb-4 ${dotColor(level)}`} />
    <p className="text-slate-400 text-xs uppercase tracking-wider">{label}</p>
    <p className="font-heading font-bold text-2xl text-white mt-1.5">{value}</p>
  </motion.div>
);

const benefits = [
  "Audit energetico completo", "Piano di efficientamento", "Priorità degli interventi",
  "Simulazioni economiche", "ROI e tempo di rientro", "Incentivi disponibili",
  "Report PDF professionale",
];

export default function Dashboard() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/leads/${leadId}`).then((r) => setLead(r.data)).catch(() => setErr(true));
  }, [leadId]);

  if (err) return <Centered>Lead non trovato.</Centered>;
  if (!lead) return <Centered>Caricamento dashboard…</Centered>;

  const p = lead.preliminary || {};
  const e = lead.energy_data || {};
  const g = lead.gas_data || {};

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 glass">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <span className="text-xs text-slate-400">{lead.company?.ragione_sociale}</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold-500/30 bg-gold-500/5 px-3 py-1 text-xs text-gold-300 mb-3">
            <Gauge className="h-3.5 w-3.5" /> Preliminary Energy Score
          </div>
          <h1 className="font-heading font-black text-3xl lg:text-4xl text-white tracking-tight">
            La prima valutazione della tua azienda è pronta
          </h1>
          <p className="text-slate-400 mt-2">{lead.company?.settore} · {lead.company?.provincia}</p>
        </motion.div>

        {/* Score + metrics */}
        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          <div className="card-dark rounded-2xl p-6 flex flex-col items-center justify-center glow-gold">
            <ScoreGauge value={p.efficiency_score} label="Efficienza energetica" />
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
            <MetricCard icon={Wallet} label="Potenziale risparmio / anno" value={fmtEur(p.savings_potential_eur_year)} level={p.savings_level} delay={0.05} />
            <MetricCard icon={Leaf} label="Possibile riduzione CO₂" value={`${fmtNum(p.co2_reduction_tons)} t`} level={p.co2_level} delay={0.1} />
            <MetricCard icon={TrendingDown} label="Investimento stimato" value={cap(p.investment_level)} level="orange" delay={0.15} />
            <MetricCard icon={TrendingUp} label="Probabilità di miglioramento" value={p.improvement_probability || "Alta"} level="blue" delay={0.2} />
          </div>
        </div>

        {/* Insights */}
        <div className="mt-8 card-dark rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <Lightbulb className="h-5 w-5 text-amber-400" />
            <h2 className="font-heading font-bold text-lg text-white">
              Abbiamo individuato {p.insights ? p.insights.length : (p.improvement_areas_count || 0)} possibili aree di miglioramento
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {(p.insights || []).map((ins, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}
                className="flex items-start gap-3 rounded-xl bg-white/[0.02] border border-white/5 p-3.5">
                <AlertTriangle className="h-4 w-4 text-amber-400/80 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-300 leading-snug">{ins}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Extracted data preview */}
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <DataBlock icon={Zap} title="Dati estratti · Energia elettrica"
            badge={e.consumo_annuo_kwh ? (
              e.fonte_consumo === "bolletta" ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded-full" data-testid="reliability-badge">
                  <BadgeCheck className="h-3 w-3" /> Dato verificato dalla bolletta
                </span>
              ) : e.fonte_consumo === "mista" ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded-full" data-testid="reliability-badge">
                  <BadgeCheck className="h-3 w-3" /> Parzialmente verificato
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded-full" data-testid="reliability-badge">
                  <Sparkles className="h-3 w-3" /> Stima AI
                </span>
              )
            ) : null}
            rows={[
            ["Contatori (POD)", e.num_contatori ? `${e.num_contatori}` : null],
            ["POD", e.pod], ["Consumo annuo", e.consumo_annuo_kwh ? `${fmtNum(e.consumo_annuo_kwh)} kWh` : null],
            ["Potenza impegnata", e.potenza_impegnata_kw ? `${fmtNum(e.potenza_impegnata_kw, 1)} kW` : null],
            ["Prezzo medio", e.prezzo_medio_eur_kwh ? `${fmtNum(e.prezzo_medio_eur_kwh, 3)} €/kWh` : null],
            ["Costo totale", e.costo_totale_eur ? fmtEur(e.costo_totale_eur) : null],
            ...((e.f1_kwh || e.f2_kwh || e.f3_kwh) ? [
              ["Consumo fascia F1", e.f1_kwh ? `${fmtNum(e.f1_kwh)} kWh` : "–"],
              ["Consumo fascia F2", e.f2_kwh ? `${fmtNum(e.f2_kwh)} kWh` : "–"],
              ["Consumo fascia F3", e.f3_kwh ? `${fmtNum(e.f3_kwh)} kWh` : "–"],
            ] : []),
          ]} />
          <DataBlock icon={Fuel} title="Dati estratti · Gas" empty="Bolletta gas non caricata" rows={[
            ["Contatori (PDR)", g.num_contatori ? `${g.num_contatori}` : null],
            ["PDR", g.pdr], ["Consumo annuo", g.consumo_annuo_smc ? `${fmtNum(g.consumo_annuo_smc)} Smc` : null],
            ["Prezzo medio", g.prezzo_medio_eur_smc ? `${fmtNum(g.prezzo_medio_eur_smc, 3)} €/Smc` : null],
            ["Costo totale", g.costo_totale_eur ? fmtEur(g.costo_totale_eur) : null],
          ]} />
        </div>

        {/* Dettaglio per contatore (POD) — utile per aziende multi-sito */}
        {(lead.energy_bills || []).filter((b) => b && b.pod && (b.costo_totale_eur || b.consumo_annuo_kwh)).length > 1 && (
          <div className="mt-6 card-dark rounded-2xl p-6" data-testid="pod-cost-breakdown">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="h-5 w-5 text-gold-400" />
              <h2 className="font-heading font-bold text-lg text-white">Dettaglio per contatore (POD)</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 text-xs uppercase tracking-wider border-b border-white/10">
                    <th className="text-left font-medium py-2 pr-4">POD</th>
                    <th className="text-right font-medium py-2 px-4">Consumo annuo</th>
                    <th className="text-right font-medium py-2 pl-4">Costo annuo</th>
                  </tr>
                </thead>
                <tbody>
                  {(lead.energy_bills || []).filter((b) => b && b.pod).map((b, i) => (
                    <tr key={b.pod || i} className="border-b border-white/5" data-testid={`pod-row-${i}`}>
                      <td className="py-2.5 pr-4 font-mono text-slate-200">{b.pod}</td>
                      <td className="py-2.5 px-4 text-right text-slate-300">{b.consumo_annuo_kwh ? `${fmtNum(b.consumo_annuo_kwh)} kWh` : "—"}</td>
                      <td className="py-2.5 pl-4 text-right font-semibold text-gold-300">{b.costo_totale_eur ? fmtEur(b.costo_totale_eur) : "—"}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-white/15">
                    <td className="py-2.5 pr-4 font-bold text-white">Totale ({e.num_contatori} POD)</td>
                    <td className="py-2.5 px-4 text-right font-bold text-white">{e.consumo_annuo_kwh ? `${fmtNum(e.consumo_annuo_kwh)} kWh` : "—"}</td>
                    <td className="py-2.5 pl-4 text-right font-bold text-gold-400">{e.costo_totale_eur ? fmtEur(e.costo_totale_eur) : "—"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mt-10 rounded-3xl border border-gold-500/20 bg-gradient-to-br from-gold-500/[0.07] to-transparent p-8 lg:p-10 relative overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-8 items-center relative">
            <div>
              <FileBarChart className="h-8 w-8 text-gold-400 mb-4" />
              <h2 className="font-heading font-black text-2xl lg:text-3xl text-white leading-tight">
                Vuoi conoscere il potenziale completo della tua azienda?
              </h2>
              <p className="text-slate-300 mt-3 text-sm leading-relaxed">
                Per realizzare un <b className="text-white">Audit Energetico professionale</b> completiamo insieme alcune
                informazioni con un'intervista guidata. Tempo richiesto: circa <b className="text-white">6 minuti</b>.
              </p>
              <Button data-testid="start-audit-btn" onClick={() => navigate(`/audit/${leadId}`)}
                className="mt-6 h-12 px-6 rounded-xl bg-gold-500 hover:bg-gold-400 text-[#0A1A2F] font-semibold group">
                Avvia l'audit approfondito
                <ArrowRight className="h-4.5 w-4.5 ml-1.5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-2.5">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/5 px-4 py-2.5">
                  <CheckCircle2 className="h-4.5 w-4.5 text-gold-400 shrink-0" />
                  <span className="text-sm text-slate-200">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : "–";

const DataBlock = ({ icon: Icon, title, rows, empty, badge }) => {
  const has = rows.some(([, v]) => v);
  return (
    <div className="card-dark rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Icon className="h-4.5 w-4.5 text-gold-400" />
        <h3 className="text-white font-medium text-sm">{title}</h3>
        {badge}
      </div>
      {!has && empty ? (
        <p className="text-slate-600 text-sm">{empty}</p>
      ) : (
        <div className="space-y-2.5">
          {rows.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 pb-2">
              <span className="text-slate-500 shrink-0">{k}</span>
              <span className="text-slate-200 font-mono-plex text-right whitespace-nowrap">{v || "–"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Centered = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center text-slate-500">{children}</div>
);
